import React, {useEffect, useRef, useState} from 'react';
import {Button} from 'primereact/button';
import {Message} from 'primereact/message';

import {Loading} from '../../utils/InlineComponents';
import {MarketplaceProductService} from '../../service/MarketplaceProductService';
import MarketplaceConnectStep, {MARKETPLACES} from './MarketplaceConnectStep';

const marketplaceProductService = new MarketplaceProductService();

// Read-only logo-or-initial badge for the summary card below, reusing the
// MARKETPLACES metadata from MarketplaceConnectStep. Kept separate from that
// file's MarketplaceCard, which owns click/keyboard/active-selection
// semantics that don't apply to this static "here's what's connected" readout.
// Exported so MarketplaceProductsStep's summary panel (Task 18) can reuse the
// exact same read-only badge instead of a third copy of the same logic.
export const MarketplaceBadge = ({marketplace}) => {
    const [logoFailed, setLogoFailed] = useState(false);

    return (
        <div style={{display: 'flex', alignItems: 'center', gap: 10}}>
            {logoFailed
                ? <span style={{
                    width: 38, height: 38, borderRadius: 10, flexShrink: 0,
                    background: marketplace.color, color: '#fff', fontWeight: 700,
                    display: 'flex', alignItems: 'center', justifyContent: 'center'}}>
                    {marketplace.initial}
                  </span>
                : <img src={`/img/marketplaces/${marketplace.key}.png`} alt={marketplace.label}
                       onError={() => setLogoFailed(true)}
                       style={{width: 38, height: 38, borderRadius: 10, objectFit: 'contain', flexShrink: 0}}/>}
            <span style={{fontWeight: 700, color: '#111827'}}>{marketplace.label}</span>
        </div>
    );
};

// Small pill communicating whether the campaign's marketplace connection is
// live. Uses a subtle tinted background rather than PrimeReact's default
// Tag (a loud solid fill in this theme -- see saga-blue's .p-tag-success),
// matching the tinted-panel convention already used by the sibling
// connected-state panels in MarketplacePriceCheckStep.js / MarketplaceProductsStep.js.
const ConnectionStatusPill = ({connected, t}) => {
    const style = connected
        ? {bg: '#e9f8ef', color: '#16a34a', icon: 'pi-check-circle',
           label: t('onboarding.integration.connectedStatus', 'Connected')}
        : {bg: '#fff4e5', color: '#d97706', icon: 'pi-exclamation-circle',
           label: t('onboarding.integration.notConnectedStatus', 'Not connected — finish setup')};
    return (
        <span style={{
            display: 'inline-flex', alignItems: 'center', gap: 6, flexShrink: 0,
            padding: '4px 12px', borderRadius: 999, fontSize: '.8rem', fontWeight: 600,
            background: style.bg, color: style.color}}>
            <i className={`pi ${style.icon}`}/>
            {style.label}
        </span>
    );
};

// Credentials step for marketplace API campaigns (see onboardingConfig.js
// stepsForUsageType / OnboardingStepContent.js). One consolidated panel:
// marketplace identity + connection-status pill up top, then the credentials
// form (embedding the ref-driven MarketplaceConnectStep, shared as-is with the
// wizard's mp_connect step). Once a marketplace is connected, the embedded
// step is locked to it via lockedMarketplace (Task 17) -- switching
// marketplace on an already-connected campaign isn't supported here. Its own
// marketplace-picker card is also suppressed then (hideMarketplacePicker),
// since the identity badge above already shows the same logo+name -- see
// MarketplaceConnectStep.js for the guard that keeps the wizard's unlocked,
// both-cards-selectable usage unaffected.
// Product import lives in its own "marketplace_products" step
// (MarketplaceProductsStep), which follows this one in the step list.
const MarketplaceIntegrationStep = ({t, campaignId}) => {
    const [integration, setIntegration] = useState(null);
    // Starts true (not false): this component only ever mounts with a real
    // campaignId (OnboardingPage.js already gates the whole page on that), so
    // a load is always about to happen. Starting false left a one-render gap
    // where loading was false AND integration was still null -- during that
    // gap marketplaceKey was undefined, so the embedded MarketplaceConnectStep
    // below would have rendered unlocked (both cards clickable) before we
    // actually knew the campaign's marketplace (Task 17 review fix).
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [saved, setSaved] = useState(false);
    const connectRef = useRef(null);

    const load = () => {
        if (!campaignId) return;
        setLoading(true);
        marketplaceProductService.getIntegration(campaignId)
            .then(result => setIntegration(result))
            .catch(() => {})
            .finally(() => setLoading(false));
    };

    useEffect(() => {
        load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [campaignId]);

    const marketplaceKey = integration?.marketplace;
    const marketplaceMeta = MARKETPLACES.find(m => m.key === marketplaceKey);
    const credentials = (marketplaceKey && integration?.credentials?.[marketplaceKey]) || null;

    // MarketplaceConnectStep already shows its own inline error Message when
    // its save() fails (see that component's `error` state), so failure
    // feedback doesn't need duplicating here -- only success does, since the
    // embedded step has no "saved" indicator of its own (it normally just lets
    // the wizard advance to the next step on success).
    const handleSave = () => {
        const api = connectRef.current;
        if (!api || !api.save) return;
        setSaving(true);
        setSaved(false);
        Promise.resolve(api.save()).then(ok => {
            setSaving(false);
            if (ok) {
                setSaved(true);
                load(); // refresh the summary card + has_password with the freshly saved values
            }
        }).catch(() => setSaving(false));
    };

    return (
        <div>
            <div style={{border: '1px solid #e5e7eb', borderRadius: 12, padding: 20, background: '#fff', maxWidth: 560}}>
                {loading
                    ? <div style={{textAlign: 'center', padding: '2em 1em'}}>
                        <Loading/>
                        <div style={{marginTop: 12, color: '#6b7280'}}>{t('onboarding.loading', 'Loading…')}</div>
                      </div>
                    // Wait for our own load() to resolve before mounting the embedded
                    // step: it derives lockedMarketplace from marketplaceKey, and
                    // mounting it earlier (marketplaceKey still undefined) would show
                    // its picker as unlocked for an already-connected campaign -- see
                    // the `loading` init comment above (Task 17 review fix).
                    : <>
                        {/* Identity, shown once, plus a status pill -- replaces the old
                            separate "Connected marketplace" card (redundant with the
                            locked picker card MarketplaceConnectStep used to also show). */}
                        <div style={{display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12}}>
                            {marketplaceMeta
                                ? <MarketplaceBadge marketplace={marketplaceMeta}/>
                                : <span style={{fontWeight: 700, color: '#111827'}}>{t('wizard.mpConnect.marketplacePicker.label')}</span>}
                            <ConnectionStatusPill connected={!!marketplaceMeta} t={t}/>
                        </div>

                        {credentials &&
                            <div style={{marginTop: 12, display: 'flex', flexDirection: 'column', gap: 4, fontSize: '.85rem', color: '#374151'}}>
                                {/* merchant_id only exists for Trendyol/Hepsiburada (Amazon's
                                    identity field is seller_id instead -- see CREDENTIAL_FIELDS
                                    in service_marketplace_campaign.py) -- guarded the same way
                                    as the merchant_name row below it, rather than rendered
                                    unconditionally, which used to leave an untranslated
                                    "field.amazon.merchant_id:" label with no value on the
                                    Amazon card. */}
                                {credentials.merchant_id &&
                                    <div>
                                        <span style={{color: '#6b7280'}}>{t(`wizard.mpConnect.field.${marketplaceKey}.merchant_id`)}: </span>
                                        {credentials.merchant_id}
                                    </div>}
                                {marketplaceKey === 'amazon' && credentials.seller_id &&
                                    <div>
                                        <span style={{color: '#6b7280'}}>{t('wizard.mpConnect.field.amazon.seller_id')}: </span>
                                        {credentials.seller_id}
                                    </div>}
                                {/* Amazon's store name is fetched from the marketplace on connect
                                    rather than typed, so showing it here is how a customer confirms
                                    we resolved the right seller account. */}
                                {(marketplaceKey === 'hepsiburada' || marketplaceKey === 'amazon') && credentials.merchant_name &&
                                    <div>
                                        <span style={{color: '#6b7280'}}>{t(`wizard.mpConnect.field.${marketplaceKey}.merchant_name`)}: </span>
                                        {credentials.merchant_name}
                                    </div>}
                            </div>}

                        {integration?.connection_error &&
                            <div style={{marginTop: 12}}>
                                <Message severity="warn" text={t('onboarding.integration.connectionError',
                                    'Connection error — please re-enter your credentials.')}/>
                            </div>}

                        {!marketplaceMeta &&
                            <div style={{marginTop: 12}}>
                                <Message severity="info" text={t('onboarding.integration.notConnected',
                                    'Not connected yet. Choose a marketplace below and enter your credentials.')}/>
                            </div>}

                        <div style={{marginTop: 20, paddingTop: 20, borderTop: '1px solid #f0f1f3'}}>
                            <div style={{fontWeight: 600, color: '#374151', marginBottom: 12}}>
                                {t('onboarding.integration.credentialsTitle', 'Update credentials')}
                            </div>
                            {/* hideMarketplacePicker: once connected (marketplaceKey truthy,
                                so the embedded step is also locked to it), its own picker
                                card is suppressed -- the badge above already shows the same
                                logo+name. Pre-connect, marketplaceKey is falsy, so this is
                                false and the full (both-cards) picker still renders here,
                                unchanged, since that's how the user actually connects. */}
                            <MarketplaceConnectStep t={t} campaignId={campaignId} ref={connectRef}
                                                    lockedMarketplace={marketplaceKey}
                                                    hideMarketplacePicker={!!marketplaceKey}/>
                            {saved &&
                                <div style={{marginTop: 12}}>
                                    <Message severity="success" text={t('onboarding.integration.saveSuccess', 'Credentials saved.')}/>
                                </div>}
                            <div style={{marginTop: 16}}>
                                <Button label={t('actions.save')} icon="pi pi-save" loading={saving} onClick={handleSave}/>
                            </div>
                        </div>
                      </>}
            </div>
        </div>
    );
};

export default MarketplaceIntegrationStep;
